import React from 'react';
import { Box, Paper, Typography, Chip, Tooltip } from '@mui/material';
import {
  AccountBalance, TrendingUp, CheckCircle, Warning,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { RATING, fmt, fmtPct, flowColor } from './constants';

const FlowTab = ({
  flowSorted, maxFlow, topInflow, outflow, strongCount, bestMom, items, rankings,
  expandedIcb, toggleLeaders,
}) => {
  const { t } = useTranslation();

  if (!items.length) return null;

  return (
    <Box>
      {/* Summary cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: { xs: 1.5, md: 2 }, mb: 2 }}>
        {[
          {
            Icon: AccountBalance,
            label: t('icb.summary.topInflow'),
            color: '#059669',
            value: topInflow?.name_vi || topInflow?.name_en || '-',
            sub: `Flow: ${fmt(topInflow?.foreign_flow_score)} | ${fmtPct(topInflow?.avg_change_5d)} (5D)`,
            isText: true,
          },
          {
            Icon: TrendingUp,
            label: t('icb.summary.topMomentum'),
            color: '#3b82f6',
            value: bestMom?.name_vi || bestMom?.name_en || '-',
            sub: `Mom: ${fmt(bestMom?.momentum_score)} | RS: ${fmt(bestMom?.rs_score)}`,
            isText: true,
          },
          {
            Icon: CheckCircle,
            label: t('icb.summary.strongGroups'),
            color: '#8b5cf6',
            value: `${strongCount} / ${items.length}`,
            sub: `${t('icb.summary.neutralCount', { count: items.filter(i => i.rating === 'NEUTRAL').length })} · ${outflow.length} ${t('icb.summary.lowFlowSuffix')}`,
          },
          {
            Icon: Warning,
            label: t('icb.summary.outflowAlert'),
            color: '#dc2626',
            value: outflow.length > 0 ? outflow.map(o => o.name_vi || o.name_en).slice(0, 2).join(', ') : t('icb.summary.noOutflow'),
            sub: outflow.length > 2
              ? t('icb.summary.andOthersBelow30', { count: outflow.length - 2 })
              : (outflow.length > 0 ? t('icb.summary.flowBelow30') : t('icb.summary.allFlowsHealthy')),
            isText: true,
          },
        ].map((card, i) => (
          <Box key={i} sx={{
            position: 'relative', overflow: 'hidden',
            p: 2, borderRadius: 2.5,
            border: '1px solid', borderColor: 'divider',
            bgcolor: 'background.paper',
            transition: 'all 0.2s',
            '&:hover': { boxShadow: `0 2px 12px ${card.color}15`, borderColor: `${card.color}40` },
          }}>
            <Box sx={{ position: 'absolute', top: 0, right: 0, width: 56, height: 56, background: `radial-gradient(circle at top right, ${card.color}0a 0%, transparent 70%)` }} />
            <Box sx={{ position: 'relative', display: 'flex', alignItems: 'flex-start', gap: 1.2 }}>
              <Box sx={{
                width: 34, height: 34, borderRadius: 1.5, flexShrink: 0,
                background: `linear-gradient(135deg, ${card.color} 0%, ${card.color}b3 100%)`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: `0 2px 6px ${card.color}4d`,
              }}>
                <card.Icon sx={{ fontSize: 18, color: '#fff' }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: '0.58rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#94a3b8', lineHeight: 1, mb: 0.4 }}>
                  {card.label}
                </Typography>
                <Typography sx={{
                  fontSize: card.isText ? '0.88rem' : '1.5rem',
                  fontWeight: card.isText ? 700 : 900,
                  color: card.color,
                  lineHeight: 1.25,
                  fontFeatureSettings: '"tnum"',
                  display: '-webkit-box',
                  WebkitLineClamp: card.isText ? 2 : 1,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  wordBreak: 'break-word',
                }}>
                  {card.value}
                </Typography>
                <Typography sx={{ fontSize: '0.62rem', color: '#94a3b8', lineHeight: 1.35, mt: 0.4 }}>
                  {card.sub}
                </Typography>
              </Box>
            </Box>
          </Box>
        ))}
      </Box>

      {/* Flow bars */}
      <Paper elevation={0} sx={{ overflow: 'hidden', borderRadius: 2, border: '1px solid #e2e8f0' }}>
        <Box sx={{ p: 1.5, borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="subtitle2" fontWeight={700}>{t('icb.flow.title')}</Typography>
            <Typography variant="caption" color="text.secondary">
              {t('icb.flow.subtitle')}
            </Typography>
          </Box>
          <Chip label={rankings?.trade_date || ''} size="small" color="primary" variant="outlined" />
        </Box>

        {/* Header row */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 0.6, px: 2, bgcolor: '#f8fafc', borderBottom: '2px solid #e2e8f0', fontSize: '0.68rem', color: '#94a3b8', fontWeight: 700, letterSpacing: '0.03em', textTransform: 'uppercase' }}>
          <Typography sx={{ width: 26, fontSize: 'inherit', fontWeight: 'inherit', color: 'inherit', textAlign: 'center', flexShrink: 0 }}>#</Typography>
          <Typography sx={{ width: 190, fontSize: 'inherit', fontWeight: 'inherit', color: 'inherit', flexShrink: 0 }}>{t('icb.flow.headerGroup')}</Typography>
          <Typography sx={{ width: 82, fontSize: 'inherit', fontWeight: 'inherit', color: 'inherit', flexShrink: 0, textAlign: 'center' }}>{t('icb.flow.headerRating')}</Typography>
          <Typography sx={{ flex: 1, fontSize: 'inherit', fontWeight: 'inherit', color: 'inherit' }}>{t('icb.flow.headerScore')}</Typography>
          <Typography sx={{ width: 40, fontSize: 'inherit', fontWeight: 'inherit', color: 'inherit', textAlign: 'right', flexShrink: 0 }}>{t('icb.flow.headerMom')}</Typography>
          <Typography sx={{ width: 52, fontSize: 'inherit', fontWeight: 'inherit', color: 'inherit', textAlign: 'right', flexShrink: 0 }}>{t('icb.flow.header1D')}</Typography>
          <Typography sx={{ width: 52, fontSize: 'inherit', fontWeight: 'inherit', color: 'inherit', textAlign: 'right', flexShrink: 0 }}>{t('icb.flow.header5D')}</Typography>
          <Typography sx={{ width: 56, fontSize: 'inherit', fontWeight: 'inherit', color: 'inherit', textAlign: 'right', flexShrink: 0 }}>{t('icb.flow.header20D')}</Typography>
          <Typography sx={{ width: 52, fontSize: 'inherit', fontWeight: 'inherit', color: 'inherit', textAlign: 'center', flexShrink: 0 }}>{t('icb.flow.headerTopStock')}</Typography>
        </Box>

        {flowSorted.map((item) => {
          const pct = maxFlow > 0 ? (item.foreign_flow_score / maxFlow) * 100 : 0;
          const barColor = flowColor(item.foreign_flow_score);
          const rc = RATING[item.rating] || RATING.NEUTRAL;
          const isOpen = expandedIcb === item.icb_code;
          const rowIdx = flowSorted.indexOf(item);
          return (
            <Tooltip key={item.icb_code} title={item.name_en || ''} placement="top-start" arrow>
              <Box
                onClick={() => toggleLeaders(item.icb_code)}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 2, py: 0.9, px: 2,
                  borderBottom: '1px solid #f1f5f9',
                  cursor: 'pointer',
                  bgcolor: isOpen ? '#eff6ff' : (rowIdx % 2 === 0 ? '#fff' : '#fafbfc'),
                  '&:hover': { bgcolor: isOpen ? '#dbeafe' : '#f0f9ff' },
                  borderLeft: isOpen ? '3px solid #6366f1' : '3px solid transparent',
                  transition: 'background 0.12s, border-color 0.12s',
                }}
              >
                <Typography sx={{ width: 26, fontWeight: 700, color: '#94a3b8', fontSize: '0.75rem', textAlign: 'center', flexShrink: 0 }}>
                  #{item.rank}
                </Typography>
                <Box sx={{ width: 190, flexShrink: 0, minWidth: 0 }}>
                  <Typography fontWeight={700} fontSize="0.84rem" noWrap color="#1e293b">{item.name_vi || item.name_en}</Typography>
                </Box>
                <Box sx={{ width: 82, flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
                  <Chip label={t(`icb.rating.${item.rating || 'NEUTRAL'}`)} size="small"
                    sx={{ bgcolor: rc.bg, color: rc.color, fontWeight: 700, fontSize: '0.62rem', height: 20, maxWidth: 82, '& .MuiChip-label': { px: 0.8 } }} />
                </Box>
                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                  <Typography sx={{ fontSize: '1rem', fontWeight: 800, color: barColor, lineHeight: 1, flexShrink: 0, minWidth: 36, textAlign: 'right', fontFeatureSettings: '"tnum"' }}>
                    {fmt(item.foreign_flow_score)}
                  </Typography>
                  <Box sx={{ flex: 1, height: 4, bgcolor: '#f1f5f9', borderRadius: 2 }}>
                    <Box sx={{ width: `${Math.max(pct, 2)}%`, height: '100%', bgcolor: barColor, borderRadius: 2, transition: 'width 0.5s ease' }} />
                  </Box>
                </Box>
                <Tooltip title={t('icb.flow.momentumTooltip')} arrow>
                  <Typography sx={{ width: 40, textAlign: 'right', flexShrink: 0, fontSize: '0.8rem', fontWeight: 700, color: item.momentum_score >= 50 ? '#10b981' : '#f59e0b', fontFeatureSettings: '"tnum"' }}>
                    {fmt(item.momentum_score)}
                  </Typography>
                </Tooltip>
                <Typography sx={{ width: 52, textAlign: 'right', flexShrink: 0, fontSize: '0.8rem', fontWeight: 600, color: (item.avg_change_1d ?? 0) >= 0 ? '#10b981' : '#ef4444', fontFeatureSettings: '"tnum"' }}>
                  {fmtPct(item.avg_change_1d)}
                </Typography>
                <Typography sx={{ width: 52, textAlign: 'right', flexShrink: 0, fontSize: '0.8rem', fontWeight: 600, color: (item.avg_change_5d ?? 0) >= 0 ? '#10b981' : '#ef4444', fontFeatureSettings: '"tnum"' }}>
                  {fmtPct(item.avg_change_5d)}
                </Typography>
                <Typography sx={{ width: 56, textAlign: 'right', flexShrink: 0, fontSize: '0.8rem', fontWeight: 600, color: (item.avg_change_20d ?? 0) >= 0 ? '#10b981' : '#ef4444', fontFeatureSettings: '"tnum"' }}>
                  {fmtPct(item.avg_change_20d)}
                </Typography>
                <Tooltip title={t('icb.flow.topGainerLoserTooltip', { gainer: item.top_gainer || '-', loser: item.top_loser || '-' })} arrow>
                  <Box sx={{ width: 52, flexShrink: 0, textAlign: 'center' }}>
                    {item.top_gainer && (
                      <Chip label={item.top_gainer} size="small"
                        sx={{ bgcolor: '#dcfce7', color: '#15803d', fontWeight: 700, fontSize: '0.65rem', height: 20 }} />
                    )}
                  </Box>
                </Tooltip>
              </Box>
            </Tooltip>
          );
        })}
      </Paper>
    </Box>
  );
};

export default FlowTab;
